/**
 * Swym Wishlist integration.
 *
 * - Waits for Swym SDK (`window._swat`) via `window.SwymCallbacks`.
 * - Hydrates every `.js-wishlist-button` on the page from the customer's
 *   saved list (toggles `.is-saved` + `aria-pressed`).
 * - Delegated click handler on `[data-wishlist-button]` adds/removes the item.
 * - Updates the header count badge (`[data-header-wishlist-count]`).
 * - Listens for variant changes so the PDP/quick-view buttons track the
 *   currently selected variant.
 * - Re-syncs after DOM mutations (collection filters, infinite scroll).
 *
 * Required per-button data attributes:
 *   data-wishlist-button       (boolean hook)
 *   data-product-id            empi (product id)
 *   data-variant-id            epi  (variant id, optional but recommended)
 *   data-product-url           absolute URL
 *   data-product-title
 *   data-product-image
 *   data-product-price         (decimal string, no currency)
 */
(function () {
  'use strict';

  const BUTTON_SELECTOR = '[data-wishlist-button]';
  const COUNT_SELECTOR = '[data-header-wishlist-count]';
  const HIDDEN_COUNT_CLASS = 'header__wishlist-count--hidden';
  const SAVED_CLASS = 'is-saved';

  // Tracks saved items keyed by stringified product id (empi).
  // Each value is an object: { epi, du } so we can render saved-state, remove
  // items, and feed product handles to the wishlist page.
  const savedMap = new Map();

  let swat = null;
  let isReady = false;

  // Set window.WISHLIST_DEBUG = true in DevTools to enable verbose logging.
  function log() {
    if (window.WISHLIST_DEBUG) {
      // eslint-disable-next-line no-console
      console.log.apply(console, ['[wishlist]'].concat([].slice.call(arguments)));
    }
  }

  // ---------------------------------------------------------------------------
  // SDK bootstrap
  //
  // Two paths:
  // 1. Push into window.SwymCallbacks — Swym fires queued callbacks on init,
  //    OR invokes them immediately if the SDK already initialised.
  // 2. As a safety net, poll for window._swat — covers the case where Swym
  //    initialised before our script loaded and silently dropped the queue.
  // ---------------------------------------------------------------------------

  function onSwymReady(callback) {
    let resolved = false;

    function resolve(instance) {
      if (resolved) return;
      resolved = true;
      swat = instance || window._swat;
      log('SDK ready', swat && Object.keys(swat).length, 'methods');
      callback();
    }

    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(resolve);

    // Polling fallback: 100ms interval, give up after 15s.
    const start = Date.now();
    const interval = setInterval(function () {
      if (window._swat && typeof window._swat.fetch === 'function') {
        clearInterval(interval);
        resolve(window._swat);
      } else if (Date.now() - start > 15000) {
        clearInterval(interval);
        // eslint-disable-next-line no-console
        console.warn('[wishlist] Swym SDK never initialised — _swat missing.');
      }
    }, 100);
  }

  // ---------------------------------------------------------------------------
  // List sync
  // ---------------------------------------------------------------------------

  function fetchList() {
    if (!swat || typeof swat.fetch !== 'function') {
      log('fetchList skipped: swat not ready');
      return;
    }
    try {
      swat.fetch(
        function (items) {
          log('fetchList success', items);
          savedMap.clear();
          if (Array.isArray(items)) {
            items.forEach(function (item) {
              // Swym item shape: { empi, epi, du, dt, iu, pr, ... }
              if (item && item.empi != null) {
                savedMap.set(String(item.empi), {
                  epi: item.epi != null ? item.epi : null,
                  du: item.du || ''
                });
              }
            });
          }
          renderAllButtons();
          renderCount();
        },
        function (err) {
          // eslint-disable-next-line no-console
          console.warn('[wishlist] fetch failed', err);
        }
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[wishlist] fetch threw', err);
    }
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  function renderButton(button) {
    const productId = button.getAttribute('data-product-id');
    if (!productId) return;
    const isSaved = savedMap.has(String(productId));
    button.classList.toggle(SAVED_CLASS, isSaved);
    button.setAttribute('aria-pressed', isSaved ? 'true' : 'false');

    const labelSaved = button.querySelector('[data-label-saved]');
    const labelUnsaved = button.querySelector('[data-label-unsaved]');
    if (labelSaved && labelUnsaved) {
      labelSaved.hidden = !isSaved;
      labelUnsaved.hidden = isSaved;
    }
  }

  function renderAllButtons(scope) {
    const root = scope || document;
    root.querySelectorAll(BUTTON_SELECTOR).forEach(renderButton);
  }

  function renderButtonsForProduct(productId) {
    document
      .querySelectorAll(BUTTON_SELECTOR + '[data-product-id="' + productId + '"]')
      .forEach(renderButton);
  }

  function renderCount() {
    const count = savedMap.size;
    document.querySelectorAll(COUNT_SELECTOR).forEach(function (el) {
      el.textContent = count < 100 ? String(count) : '99+';
      el.classList.toggle(HIDDEN_COUNT_CLASS, count === 0);
    });
  }

  // ---------------------------------------------------------------------------
  // Add / remove
  // ---------------------------------------------------------------------------

  function buildProductPayload(button) {
    const empi = parseInt(button.getAttribute('data-product-id'), 10);
    if (!empi) return null;
    const epiAttr = button.getAttribute('data-variant-id');
    const epi = epiAttr ? parseInt(epiAttr, 10) : empi;
    return {
      empi: empi,
      epi: epi,
      du: button.getAttribute('data-product-url') || window.location.href,
      dt: button.getAttribute('data-product-title') || '',
      iu: button.getAttribute('data-product-image') || '',
      pr: parseFloat(button.getAttribute('data-product-price')) || 0
    };
  }

  function handleAdd(button) {
    if (!swat || typeof swat.addToWishList !== 'function') {
      // eslint-disable-next-line no-console
      console.warn('[wishlist] addToWishList unavailable on _swat', swat);
      return;
    }
    const payload = buildProductPayload(button);
    if (!payload) return;
    log('add', payload);
    try {
      swat.addToWishList(
        payload,
        function (response) {
          log('add success', response);
          const epi =
            response && response.epi != null ? response.epi : payload.epi;
          savedMap.set(String(payload.empi), { epi: epi, du: payload.du });
          renderButtonsForProduct(payload.empi);
          renderCount();
        },
        function (err) {
          // eslint-disable-next-line no-console
          console.warn('[wishlist] add failed', err, payload);
        }
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[wishlist] add threw', err, payload);
    }
  }

  function handleRemove(button) {
    if (!swat || typeof swat.removeFromWishList !== 'function') {
      // eslint-disable-next-line no-console
      console.warn('[wishlist] removeFromWishList unavailable on _swat', swat);
      return;
    }
    const empi = parseInt(button.getAttribute('data-product-id'), 10);
    if (!empi) return;
    const stored = savedMap.get(String(empi)) || {};
    const payload = {
      empi: empi,
      epi: stored.epi != null
        ? stored.epi
        : (parseInt(button.getAttribute('data-variant-id'), 10) || empi),
      du: stored.du || button.getAttribute('data-product-url') || window.location.href
    };
    log('remove', payload);
    try {
      swat.removeFromWishList(
        payload,
        function (response) {
          log('remove success', response);
          savedMap.delete(String(empi));
          renderButtonsForProduct(empi);
          renderCount();
          document.dispatchEvent(
            new CustomEvent('wishlist:item-removed', { detail: { empi: empi } })
          );
        },
        function (err) {
          // eslint-disable-next-line no-console
          console.warn('[wishlist] remove failed', err, payload);
        }
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[wishlist] remove threw', err, payload);
    }
  }

  // ---------------------------------------------------------------------------
  // Event delegation
  // ---------------------------------------------------------------------------

  function onClick(event) {
    const button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;
    event.preventDefault();

    log('click', button, 'isReady=', isReady);

    if (!isReady) {
      // eslint-disable-next-line no-console
      console.warn(
        '[wishlist] click ignored — Swym SDK not initialised yet. ' +
          'window._swat:',
        window._swat
      );
      return;
    }

    const productId = button.getAttribute('data-product-id');
    if (!productId) return;

    if (savedMap.has(String(productId))) {
      handleRemove(button);
    } else {
      handleAdd(button);
    }
  }

  function onVariantChange(event) {
    // The main-product section dispatches `variant:changed` on `document` with
    // `event.detail` set directly to the variant object.
    const variant = event && event.detail;
    if (!variant || variant.id == null) return;
    // PDP/quick-view buttons are scoped to the product info block. Update any
    // button on the page that targets the same product (cards on a "you may
    // also like" rail intentionally keep their own first-available variant).
    document
      .querySelectorAll(
        '.f-product-form__wishlist-btn[data-wishlist-button], ' +
          '.quick-add-modal__bookmark-btn[data-wishlist-button]'
      )
      .forEach(function (button) {
        button.setAttribute('data-variant-id', String(variant.id));
      });
  }

  // ---------------------------------------------------------------------------
  // Mutation observer — re-render when product cards are injected by
  // collection filters, infinite scroll, quick-view, etc.
  // ---------------------------------------------------------------------------

  function observeMutations() {
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches(BUTTON_SELECTOR)) {
            renderButton(node);
          } else if (node.querySelectorAll) {
            renderAllButtons(node);
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ---------------------------------------------------------------------------
  // Public hook for the wishlist page to read saved items.
  // ---------------------------------------------------------------------------

  function snapshot() {
    return {
      items: Array.from(savedMap.entries()).map(function (entry) {
        return {
          empi: parseInt(entry[0], 10),
          epi: entry[1].epi,
          du: entry[1].du
        };
      })
    };
  }

  window.WishlistAPI = {
    onReady: function (cb) {
      if (isReady) {
        cb(snapshot());
      } else {
        document.addEventListener(
          'wishlist:ready',
          function () { cb(snapshot()); },
          { once: true }
        );
      }
    },
    refresh: fetchList,
    isSaved: function (empi) {
      return savedMap.has(String(empi));
    },
    count: function () {
      return savedMap.size;
    }
  };

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  document.addEventListener('click', onClick);
  document.addEventListener('variant:changed', onVariantChange);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeMutations);
  } else {
    observeMutations();
  }

  onSwymReady(function () {
    isReady = true;
    fetchList();
    document.dispatchEvent(new CustomEvent('wishlist:ready'));
  });
})();
